import React, {Fragment, ReactNode, useEffect, useState} from "react";
import {run} from "../../utils/function-util";
import {httpPost} from "../../api/http-functions";
import {searchOrganizationMembers} from "../../api/organization-functions";
import {withLazyDynamicTable} from "../ui/hoc/LazyDynamicTable";
import {makeStyles} from "@material-ui/core/styles";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import {Typography} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Breadcrumbs from "@material-ui/core/Breadcrumbs";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import {TableLoadingRow} from "../ui/table/TableLoadingRow";
import {TableEmptyRow} from "../ui/table/TableEmptyRow";
import {ActionsMenu} from "../ui/ActionsMenu";
import UserAvatar from "../user-avatar/UserAvatar";
import {SearchedMember} from "../../models/membership.models";
import {PaginatedSearch} from "../../models/shared";
import {MoveMemberAction} from "./shared/actions/MoveMemberAction";

export const useStyles = makeStyles(theme => ({
    root: {
        height: 'auto',
        flexGrow: 1,
        maxWidth: 700,
    },
    search: {
        [theme.breakpoints.down('sm')]: {
            width: '100%'
        },
        [theme.breakpoints.up('md')]: {
            width: '40%'
        }
    },
    actionColumn: {
        maxWidth: 100,
        width: 100
    },
    avatarColumn: {
        maxWidth: 70,
        width: 70
    },
    spinner: {
        position: 'absolute',
        left: '50%'
    }
}));

type MemberDataRowProps = SearchedMember & { actions: ReactNode };

const MemberDataRow = ({
                           userId,
                           userFirstName,
                           userLastName,
                           email,
                           organizationId,
                           organizationName,
                           organizationUnitId,
                           organizationUnitName,
                           actions
                       }: MemberDataRowProps) => {
    const navigate = useNavigate();

    const goToUnit = async () => {
        const returnUrl = encodeURIComponent(`/organization-units/${organizationUnitId}/detail?tab=0`);
        navigate(`/organizations?organizationId=${organizationId}&returnUrl=${returnUrl}`);
    };

    const goToOrg = async () => {
        const returnUrl = encodeURIComponent(`/organization-units`);
        navigate(`/organizations?organizationId=${organizationId}&returnUrl=${returnUrl}`);
    }

    return (
        <TableRow>
            <TableCell component="th" scope="row"> <UserAvatar userId={userId}/></TableCell>
            <TableCell>
                <Typography variant="body1">{userFirstName} {userLastName}</Typography>
                <Typography variant="body2"
                            color={'textPrimary'}>{email}</Typography>
            </TableCell>
            <TableCell>
                <Button onClick={goToOrg}><Typography variant="body1">{organizationName}</Typography></Button>
            </TableCell>
            <TableCell>
                <Button onClick={goToUnit}>
                    <Breadcrumbs>
                        <Typography variant="body1">
                            {`../${organizationUnitName}`}
                        </Typography>
                    </Breadcrumbs>
                </Button>
            </TableCell>
            <TableCell align="center">
                {actions}
            </TableCell>
        </TableRow>
    );
};

interface _MembersProperty<T> {
    readonly loading: boolean,
    readonly data: T[],
    readonly onDataItemUpdated: (item: T, compareFn: any) => T[],
    readonly handleChange: () => void,
    readonly actions: ((d: T) => JSX.Element) | null
}

const _Members = ({
                      loading,
                      data: members,
                      handleChange,
                      onDataItemUpdated,
                      actions = null
                  }: _MembersProperty<SearchedMember>) => {
    const {t} = useTranslation();
    return (
        <Fragment>
            <TableHead>
                <TableRow>
                    <TableCell></TableCell>
                    <TableCell>
                        <label>
                            {t('Members.UserName')}
                        </label>
                    </TableCell>
                    <TableCell>
                        <label>
                            {t('Members.Organization')}
                        </label>
                    </TableCell>
                    <TableCell>
                        <label>
                            {t('Members.OrganizationUnit')}
                        </label>
                    </TableCell>
                    <TableCell style={{width: "50px"}} align="center">
                        <label>
                            {t(`Members.Actions.Title`)}
                        </label>
                    </TableCell>
                </TableRow>
            </TableHead>

            <TableBody>
                {
                    loading ?
                        <TableLoadingRow colSpan={4}/> :
                        members.length === 0 ?
                            <TableEmptyRow messageKey={'Members.NoMembers'}/> :
                            members.map((m: SearchedMember) =>
                                <MemberDataRow key={`${m.userId}-${m.organizationUnitId}`}
                                               {...m}
                                               actions={actions
                                                   ? actions(m)
                                                   : <ActionsMenu>
                                                       {
                                                           (onClose: () => void) =>
                                                               <>
                                                                   <MoveMemberAction organizationId={m.organizationId}
                                                                                     member={m}
                                                                                     onMoved={handleChange}
                                                                                     onActionCompleted={onClose}/>
                                                               </>
                                                       }
                                                   </ActionsMenu>}

                                />)
                }
            </TableBody>
        </Fragment>
    );
}

export const Members = ({
                            organizationId,
                            actions,
                            actionForAll
                        }: { organizationId?: number, actions?: (data: any) => JSX.Element, actionForAll?: JSX.Element }) => {
    const [refresh, setRefresh] = useState(0);

    useEffect(run(Promise.resolve(), () => setRefresh(r => r + 1)), [organizationId])

    const query = (args: PaginatedSearch) => searchOrganizationMembers(httpPost)({organizationId, ...args});

    const handleChange = () => setRefresh(r => r + 1);

    return withLazyDynamicTable(_Members, query, [], actionForAll)({ refresh, handleChange, actions});
}
